import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import {
  Calculator, ClipboardCheck, Bell, Wrench, Kanban, BookOpen,
  WifiOff, FileBarChart, Sparkles, Check, ArrowRight,
  ShieldCheck, Zap, Star, Search, Calendar, Users, FolderOpen,
  Receipt, BarChart3, FileSignature, Building, Flame, PlayCircle, Quote,
  Banknote, MapPinned, ScanSearch
} from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

// Détail des onglets — chaque feature a un emplacement vidéo (`videoSrc`)
// et un `slug` stable utilisé comme clé pour les overrides CMS admin.
// Source unique partagée avec AdminLandingPage : il suffit d'AJOUTER une entrée
// ici pour qu'elle apparaisse automatiquement dans le CMS Landing (sans toucher
// au code admin). Le slug NE DOIT JAMAIS changer une fois publié (sinon perte
// des overrides déjà saisis par l'admin).
export const FEATURES = [
  {
    slug: "recherche",
    icon: Search,
    title: "Recherche de biens",
    tagline: "Toutes tes annonces, un seul endroit.",
    text: "Fini les onglets ouverts partout et les fichiers Excel. Toutes les annonces qui matchent tes critères arrivent au même endroit. À l'ajout d'un bien, tu commences à taper l'adresse — l'app te propose les vraies adresses postales (comme sur un GPS), tu cliques, la ville et le code postal se remplissent tout seuls. Tu n'as plus jamais à retaper l'adresse, et le bien est localisé au mètre près pour les analyses qui suivent.",
    bullets: [
      "Saisie d'adresse intelligente : choisis, c'est rempli — fini les fautes de frappe sur les codes postaux",
      "Import automatique des annonces IAD, LeBonCoin, SeLoger, Coup de Cœur (option webhook)",
      "Filtres pour retrouver un bien en 2 secondes : ville, prix, surface, type, source",
      "Statut visite / étude / projet visible directement sur la fiche du bien",
    ],
    videoSrc: "/videos/feature-recherche.mp4",
    accent: "from-amber-500/20 to-orange-500/10",
  },
  {
    slug: "analyse-marche",
    icon: MapPinned,
    title: "Analyse marché automatique",
    tagline: "Tout savoir sur un quartier avant même d'avoir visité.",
    text: "Imagine : tu ajoutes un bien dans l'app. 10 secondes plus tard, sans cliquer nulle part, tu sais tout sur le quartier. Le numéro de parcelle cadastrale et sa surface exacte. Si le terrain est constructible ou en zone protégée. Les risques naturels recensés sur la commune (sécheresse, inondation, mouvement de terrain) et la liste des arrêtés de catastrophe naturelle des 20 dernières années. Et surtout : tous les prix au m² des ventes récentes dans un rayon de 500 mètres autour du bien. Tu sais instantanément si l'annonce est correctement positionnée — ou si tu peux négocier.",
    bullets: [
      "Détail cadastre : numéro de parcelle, section, surface — utile pour le notaire et l'acte",
      "Zonage urbanisme (PLU) : tu vois immédiatement si le terrain est constructible ou figé en zone naturelle",
      "Liste complète des risques de la commune + tous les arrêtés de catastrophe naturelle (utile pour l'assurance et négocier les diagnostics)",
      "Prix au m² du quartier basés sur les vraies ventes des 3 dernières années (données officielles publiées par l'État)",
      "Carte interactive du bien avec marqueur GPS et zone de chalandise de 250 mètres",
    ],
    videoSrc: null,
    accent: "from-amber-400/20 to-yellow-500/10",
  },
  {
    slug: "etude",
    icon: Calculator,
    title: "Étude de rentabilité",
    tagline: "Sais-tu vraiment combien tu vas gagner ? En 30 secondes.",
    text: "Tu connais le prix d'achat, la surface, l'estimation travaux. L'app fait le reste : notaire, agence, portage, intérêts, assurance emprunteur, frais de société (assurance, comptable, banque), TVA sur marge ou normale, impôt sur les sociétés progressif. Résultat en bas : la plus-value nette dans ta poche, après impôts, à l'euro près. Et pour cadrer ton prix de revente, l'app te propose automatiquement le prix moyen du quartier (basé sur les vraies ventes récentes) × ta surface, avec un bouton 'Appliquer' qui injecte la valeur dans l'étude.",
    bullets: [
      "Suggestion automatique du prix de revente cible basée sur les ventes réelles du quartier",
      "Comparaison instantanée : ta cible est-elle au-dessus ou en-dessous du marché ? De combien ?",
      "Calcul d'impôt sur les sociétés réaliste (15% jusqu'à 42 500€, 25% au-delà)",
      "Tu choisis le régime TVA adapté au projet (sur marge, classique ou exonérée)",
      "Export PDF prêt à montrer au banquier — page de garde, graphique des coûts, tableau d'impôts détaillé",
    ],
    videoSrc: "/videos/feature-etude.mp4",
    accent: "from-emerald-500/20 to-teal-500/10",
  },
  {
    slug: "visite",
    icon: ClipboardCheck,
    title: "Visites techniques",
    tagline: "Une visite de 20 minutes, un chiffrage complet derrière.",
    text: "Tu débarques sur le bien avec ton téléphone. Tu coches une checklist couvrant 13 corps de métier (électricité, plomberie, chauffage, toiture, façade, menuiserie, isolation, etc.). Pour chaque ligne, tu prends une photo et tu indiques 'OK / à refaire / urgent'. L'app pré-remplit le chiffrage à partir de tes prix d'artisans (ton bordereau). À la fin : verdict global GO / ATTENTION / NO GO, total travaux estimé, rapport PDF photo prêt à envoyer au banquier ou à comparer avec un autre bien.",
    bullets: [
      "Checklist de 13 corps de métier — rien n'est oublié, même sous pression",
      "Tu chiffres directement sur le terrain : ton bordereau de prix est dans le téléphone",
      "Photos rangées automatiquement par poste de travail (utile pour les artisans qui devisent à distance)",
      "Verdict GO / ATTENTION / NO GO en un coup d'œil — parfait pour négocier ou passer",
      "Fonctionne SANS RÉSEAU : visite tes biens isolés sans stress, tout sera synchronisé au retour",
    ],
    videoSrc: "/videos/feature-visite.mp4",
    accent: "from-blue-500/20 to-cyan-500/10",
  },
  {
    slug: "pipeline",
    icon: Kanban,
    title: "Pipeline de projets",
    tagline: "Tous tes projets en cours, sur un seul tableau visuel.",
    text: "Toutes tes opérations en cours sur un kanban : prospection, négociation, en cours d'achat, acheté, en rénovation, en vente, vendu. Tu fais glisser les cartes d'une colonne à l'autre, et toute l'app suit. Quand un compromis est signé, le bien passe en 'acheté' tout seul, les bons de commande artisans sont pré-générés. Sur chaque carte tu vois en direct le budget prévu vs le budget réel — tu repères immédiatement les projets qui dérapent.",
    bullets: [
      "Glisser-déposer entre colonnes — l'app met à jour tous les autres modules en cascade",
      "Écart prévu vs réel visible sur chaque carte : tu vois les dérives sans ouvrir le projet",
      "Quand tu signes un compromis, le projet et les BDC se mettent à jour tout seuls",
      "Cycle complet visible : du repérage du bien jusqu'à la vente finale",
    ],
    videoSrc: "/videos/feature-pipeline.mp4",
    accent: "from-purple-500/20 to-pink-500/10",
  },
  {
    slug: "offres",
    icon: FileSignature,
    title: "Offres d'achat",
    tagline: "Garde une trace de chaque offre, contre-offre et négociation.",
    text: "Tu fais une offre, l'agent répond, tu fais une contre-offre, ça discute. Au bout d'une semaine, qui se souvient de qui a dit quoi ? L'app, elle. Chaque offre est tracée avec date, montant, statut (envoyée, en négociation, acceptée, refusée) et notes. Quand une offre est acceptée, le prix d'achat réel est propagé automatiquement dans l'étude, le pipeline, et les statistiques. Le banquier verra le 'prix accepté' partout, pas le prix de l'annonce.",
    bullets: [
      "Historique complet : tu retrouves chaque contre-offre avec son contexte",
      "Une offre acceptée met à jour automatiquement le prix d'achat dans toute l'app",
      "PDF de l'offre généré, prêt à envoyer à l'agent ou au notaire",
      "Plusieurs biens en négociation en parallèle ? Aucun risque de confusion",
    ],
    videoSrc: "/videos/feature-offres.mp4",
    accent: "from-rose-500/20 to-red-500/10",
  },
  {
    slug: "bpu",
    icon: BookOpen,
    title: "Bordereau de prix",
    tagline: "Tes prix d'artisans, négociés une fois, utilisés partout.",
    text: "Le secret pour chiffrer vite et juste : avoir tes prix sous la main. Ton bordereau, c'est ton référentiel personnel — le prix au m² d'une douille, d'un mètre de cloison, d'une dalle en béton. Saisis-le une fois (ou prends en photo une notice et l'IA l'extrait pour toi), tout le reste de l'app l'utilise : la visite, l'étude, les bons de commande. Tu ne devises plus 'au pifomètre' — tu sors un chiffre solide en 30 secondes.",
    bullets: [
      "13 corps de métier déjà pré-paramétrés à ton inscription, tu n'as plus qu'à ajuster",
      "Import vidéo : montre une notice à la caméra, l'IA extrait les prix dans le bordereau",
      "Historique des prix : tu retrouves combien tu payais l'électricité il y a 6 mois",
      "Un seul référentiel pour visites + études + bons de commande = zéro incohérence",
    ],
    videoSrc: "/videos/feature-bpu.mp4",
    accent: "from-indigo-500/20 to-blue-500/10",
  },
  {
    slug: "bdc",
    icon: Receipt,
    title: "Bons de commande artisans",
    tagline: "Tes BDC générés, envoyés et validés — sans Excel.",
    text: "Quand un projet passe en rénovation, les bons de commande sont pré-créés tout seuls, un par corps de métier, avec les lignes issues de ta visite et de ton bordereau. Tu envoies un lien sécurisé à l'artisan, il complète ses prix et ses quantités, il soumet pour validation. Tu acceptes (ou tu discutes), et le BDC validé devient un PDF officiel propre, prêt à signer. Le bloc 'Maître d'Ouvrage' est rempli automatiquement avec ton identité (depuis ton profil) — fini les champs vides à signer manuellement.",
    bullets: [
      "Pré-remplissage automatique à partir de ta visite et de ton bordereau",
      "Espace artisan sécurisé : il accède uniquement à son BDC, il modifie, il soumet",
      "Lignes à 0 € automatiquement masquées sur le PDF officiel — un devis propre, lisible, professionnel",
      "PDF avec ton nom, prénom et téléphone déjà remplis dans le bloc client",
    ],
    videoSrc: "/videos/feature-bdc.mp4",
    accent: "from-amber-500/20 to-yellow-500/10",
  },
  {
    slug: "dossier-banque",
    icon: Banknote,
    title: "Dossier de financement bancaire",
    tagline: "Tu arrives chez le banquier avec un dossier de 18 pages. En 1 clic.",
    text: "Tu prends RDV avec ton banquier mercredi matin. Tu cliques sur la carte du projet dans la rubrique 'Dossier de financement' : un PDF de 18 pages se télécharge. Page 1 : page de garde avec l'adresse du bien. Page 2 : sommaire avec les numéros de page. Suivent : synthèse de l'opération, fiche détaillée du bien, plan de financement chiffré, échéancier mois par mois du prêt, étude de rentabilité avec impôts, détail des travaux par artisan, parcelle cadastrale et zonage urbanisme officiels, analyse des risques naturels du quartier, ton profil personnel (revenus, charges, patrimoine), checklist des 18 pièces à apporter (K-bis, bilans, devis...). Le banquier comprend tout sans qu'on lui explique.",
    bullets: [
      "Génération en 1 clic — pas de mise en page Word, pas de copier-coller",
      "Données toujours à jour : tu modifies un chiffre dans l'app, le PDF reflète le changement au prochain téléchargement",
      "Inclut TOUTES les données officielles du bien (cadastre, urbanisme, risques, prix marché)",
      "Checklist des 18 pièces à apporter : tu coches au fur et à mesure, tu n'oublies rien",
      "Pied de page 'Confidentiel — usage interne banque' + pagination X / Y sur toutes les pages — niveau document de cabinet pro",
    ],
    videoSrc: null,
    accent: "from-emerald-500/20 to-green-500/10",
  },
  {
    slug: "travaux",
    icon: Wrench,
    title: "Suivi de travaux",
    tagline: "Sais-tu vraiment où tu en es sur tes chantiers en cours ?",
    text: "Sur chaque chantier en cours, trois colonnes : ce qui était prévu (l'étude), ce qui est engagé (les BDC signés), ce qui est dépensé (les factures uploadées). Tu vois en temps réel si tu es dans les clous, en avance, ou en train de dérouler. Une alerte se déclenche automatiquement si tu dépasses 10% du budget initial sur un poste. Tu peux faire un drill-down poste par poste : 'combien j'ai vraiment dépensé sur l'électricité de ce projet ?'",
    bullets: [
      "Vue prévu / engagé / dépensé pour chaque chantier — la dérive se voit en un coup d'œil",
      "Alerte automatique si tu passes le seuil des +10% sur un poste",
      "Détail par corps de métier : tu sais d'où vient le dépassement",
      "Upload des factures avec reconnaissance automatique du fournisseur",
    ],
    videoSrc: "/videos/feature-travaux.mp4",
    accent: "from-orange-500/20 to-amber-500/10",
  },
  {
    slug: "calendrier",
    icon: Calendar,
    title: "Calendrier",
    tagline: "Aucune deadline ratée — visites, signatures, livraisons.",
    text: "Toutes les dates importantes d'un MdB au même endroit : RDV de visite, signatures compromis, dates butoirs des conditions suspensives, fins de chantier prévues, échéances de revente. Code couleur par type, vue mensuelle ou hebdo, création depuis n'importe quelle fiche. Tu signes un compromis, la date butoir des conditions est automatiquement créée dans le calendrier — tu ne passeras plus à côté.",
    bullets: [
      "Création automatique des deadlines à partir d'un compromis signé",
      "Code couleur : visite, travaux, signature, revente — tu identifies tout au premier regard",
      "Synchronisation possible avec Google Calendar / Apple Calendar (export iCal)",
      "Vue mensuelle ou semaine, sur ordi ou sur mobile",
    ],
    videoSrc: "/videos/feature-calendrier.mp4",
    accent: "from-cyan-500/20 to-sky-500/10",
  },
  {
    slug: "reseau",
    icon: Users,
    title: "Réseau",
    tagline: "Ton carnet d'adresses pro, organisé et exploitable.",
    text: "Artisans, notaires, courtiers, chasseurs immobiliers, agents : tous tes contacts pros classés par catégorie, avec spécialité, notes et lien direct vers les projets sur lesquels ils ont travaillé. Tu cherches l'électricien qui a fait ton dernier chantier ? Il est là, avec son téléphone, son SIRET et ses 3 derniers BDC. Plus jamais 'comment il s'appelait déjà ?'",
    bullets: [
      "Catégories standards : artisan, notaire, courtier, chasseur, autre",
      "Tu vois sur quels projets chaque contact est intervenu",
      "Un artisan ajouté ici peut recevoir un BDC directement, sans re-saisie",
      "Pré-rempli automatiquement à partir des artisans utilisés sur tes BDC",
    ],
    videoSrc: "/videos/feature-reseau.mp4",
    accent: "from-violet-500/20 to-purple-500/10",
  },
  {
    slug: "documents",
    icon: FolderOpen,
    title: "Documents",
    tagline: "Compromis, actes, factures — classés, retrouvables.",
    text: "Un coffre-fort numérique par projet : compromis, acte authentique, factures travaux, devis signés, photos avant/après. Glisse-dépose un fichier, l'app te demande de quel projet et de quelle étape il s'agit (acquisition, travaux, revente). Tu retrouves le compromis du projet d'il y a 2 ans en 5 secondes, depuis ton mobile, depuis n'importe où.",
    bullets: [
      "Stockage sécurisé dans le cloud, taille de fichier libre",
      "Classement automatique par projet et étape — tu ne ranges qu'une fois",
      "Tu conserves les versions précédentes des documents signés",
      "Accessible depuis n'importe quel appareil, hors-ligne inclus",
    ],
    videoSrc: null,
    accent: "from-slate-500/20 to-zinc-500/10",
  },
  {
    slug: "stats",
    icon: BarChart3,
    title: "Tableau de bord",
    tagline: "L'état de ta société, en un seul écran.",
    text: "Combien de projets en cours, combien vendus ce mois-ci, quelle plus-value cumulée, combien d'argent immobilisé, quel ROI moyen sur tes derniers projets. Tous les chiffres qui comptent vraiment, sur un seul écran. Chaque chiffre est cliquable : tu veux savoir d'où vient cette plus-value de 47 000€ ? Un clic, tu vois la liste des projets concernés. Plus de tableurs, plus de calculs à la main fin de mois.",
    bullets: [
      "Plus-value nette après impôts calculée projet par projet",
      "Comparaison ROI prévu (étude initiale) vs ROI réellement réalisé",
      "Solde de trésorerie société (apport + ventes − achats − dépenses)",
      "Chaque chiffre cliquable pour voir le détail (zéro Excel)",
    ],
    videoSrc: null,
    accent: "from-emerald-500/20 to-green-500/10",
  },
  {
    icon: WifiOff,
    title: "Mode hors-ligne",
    tagline: "L'app marche même au fin fond de l'Ardèche.",
    text: "Tu pars visiter un bien isolé, pas de 4G, pas de wifi. L'app continue de fonctionner comme si de rien n'était. Tu fais ta visite, tu prends 50 photos, tu remplis ton étude, tu chiffres les travaux. Tu reviens en ville, l'app se synchronise toute seule au retour du réseau, sans rien te demander. Aucune donnée perdue, aucun stress.",
    bullets: [
      "Installation en 1 clic sur smartphone (iOS / Android) et ordinateur",
      "Toutes les données critiques pré-chargées localement (visites, bordereau, biens, photos)",
      "Synchronisation automatique au retour du réseau, gestion intelligente des conflits",
      "Idéal pour les visites en zone blanche, les chantiers en sous-sol, les déplacements TGV sans wifi",
    ],
    videoSrc: null,
    accent: "from-pink-500/20 to-rose-500/10",
  },
  {
    slug: "frais",
    icon: Building,
    title: "Frais de société",
    tagline: "Tes vrais coûts intégrés à chaque étude. Marge réelle, pas fictive.",
    text: "Ton expert-comptable te coûte 200€/mois, ton assurance 80€, ton logiciel de compta 50€, ta banque 30€... Sur un projet qui dure 10 mois, ça fait 3 600€ de frais fixes que tu oublies systématiquement dans tes calculs. L'app les saisit une fois, puis les répartit automatiquement au prorata sur chaque projet — selon sa durée et combien de projets tu mènes en parallèle. Tu ne te dis plus 'j'ai gagné 50 000€' alors que tu en as gagné 46 400€.",
    bullets: [
      "Frais saisis une fois (en mensuel ou en annuel), répartis automatiquement",
      "Quote-part calculée selon durée du projet et nombre de projets en parallèle",
      "Tu vois clairement ces frais dans l'étude détaillée — pas planqués dans un calcul",
      "Marges réelles, pas fictives — utile surtout face à l'expert-comptable et au banquier",
    ],
    videoSrc: "/videos/feature-frais.mp4",
    accent: "from-teal-500/20 to-emerald-500/10",
  },
];

const PRICING_FEATURES = [
  "Projets illimités",
  "Toutes les fonctionnalités sans option à payer en plus",
  "Dossier de financement bancaire en 1 clic (18+ pages)",
  "Analyse automatique du quartier (cadastre, urbanisme, risques, prix marché)",
  "Saisie d'adresse intelligente",
  "L'app marche aussi hors-ligne (sur le terrain, en sous-sol, en TGV)",
  "Reconnaissance automatique des factures par l'IA",
  "Tous les exports PDF (visites, études, BDC, dossier banque)",
  "Espace artisan inclus (pas de licence par artisan à payer)",
  "Import automatique des annonces (option)",
  "Support par email direct",
  "Mises à jour à vie incluses",
];

// Carte par fonctionnalité — alterne droite/gauche pour la dynamique visuelle.
function FeatureRow({ feature, index }) {
  const Icon = feature.icon;
  const reverse = index % 2 === 1;
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}
      data-testid={`feature-row-${index}`}
    >
      {/* Texte */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.accent} border border-amber-500/20 flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-amber-400" />
          </div>
          <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">
            {`Onglet ${String(index + 1).padStart(2, '0')}`}
          </span>
        </div>
        <h3 className="text-3xl lg:text-4xl font-bold tracking-tight mb-3">{feature.title}</h3>
        <p className="text-amber-400 text-lg font-semibold mb-4 italic">{feature.tagline}</p>
        <p className="text-zinc-300 text-base leading-relaxed mb-5">{feature.text}</p>
        <ul className="space-y-2.5">
          {feature.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-zinc-400">
              <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Visuel : vidéo en frame téléphone (screencasts mobile) ou placeholder */}
      <div className="relative">
        <div className={`absolute -inset-4 bg-gradient-to-br ${feature.accent} rounded-[3rem] blur-3xl opacity-50 pointer-events-none`} />
        <div className="relative flex justify-center">
          {feature.videoSrc ? (
            // Phone mockup frame pour les screencasts mobiles
            <div
              className="relative rounded-[2.2rem] border-[10px] border-zinc-800 bg-zinc-950 shadow-2xl overflow-hidden"
              style={{ width: '260px', height: '563px' }}
              data-testid={`feature-video-frame-${index}`}
            >
              {/* Notch */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-zinc-900 rounded-b-2xl z-10" />
              <video
                src={feature.videoSrc}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid={`feature-video-${index}`}
              />
            </div>
          ) : (
            // Placeholder phone mockup
            <div
              className="relative rounded-[2.2rem] border-[10px] border-zinc-800 bg-zinc-950 shadow-2xl flex items-center justify-center overflow-hidden"
              style={{ width: '260px', height: '563px' }}
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-zinc-900 rounded-b-2xl z-10" />
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.accent} opacity-40`} />
              <div className="text-center relative z-10 px-6">
                <PlayCircle className="w-14 h-14 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
                <p className="text-xs text-zinc-600 font-mono">Démo bientôt</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [founders, setFounders] = useState({ remaining: 30, taken: 0, founder_price_eur: 39, standard_price_eur: 49 });
  const [cms, setCms] = useState({});
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/public/founders-count`).then(r => setFounders(r.data)).catch(() => null);
    axios.get(`${API}/public/landing-config`).then(r => setCms(r.data || {})).catch(() => null);
    axios.get(`${API}/public/reviews`).then(r => setReviews(r.data || [])).catch(() => null);
  }, []);

  const founderOpen = founders.remaining > 0;

  // Merge des overrides CMS sur les FEATURES en dur — clé = slug (stable)
  const featuresMerged = FEATURES.map((f) => {
    const ov = (cms.features || {})[f.slug] || {};
    return {
      ...f,
      title: ov.title ?? f.title,
      tagline: ov.tagline ?? f.tagline,
      text: ov.text ?? f.text,
      bullets: (Array.isArray(ov.bullets) && ov.bullets.length > 0) ? ov.bullets : f.bullets,
      // Support old (videoSrc) et new (video_url) noms côté CMS
      videoSrc: ov.video_url ?? ov.videoSrc ?? f.videoSrc,
    };
  });

  // Hero overrides
  const heroTitle1 = cms.hero_title_part1 || "L'app tout-en-un que les MDB";
  const heroHighlight = cms.hero_highlight || "attendaient";
  const heroTitle2 = cms.hero_title_part2 || "Faite par un MDB.";
  const heroSubtitle = cms.hero_subtitle || "De la première annonce au dossier banque finalisé : tu trouves ton bien, tu le chiffres, tu le visites, tu négocies, tu finances, tu rénoves, tu revends. Un seul outil, sans Excel, sans copier-coller. L'app pense comme un marchand de biens.";

  // Story overrides
  const storyParagraphs = (Array.isArray(cms.story_paragraphs) && cms.story_paragraphs.length > 0) ? cms.story_paragraphs : null;
  const storyQuote = cms.story_quote || "Aujourd'hui je l'utilise tous les jours sur mes opérations. Je l'ouvre maintenant à d'autres MDB qui en ont marre de jongler entre 5 outils mal raccordés.";

  // Pricing overrides
  const pricingIntro = cms.pricing_intro || "Un seul plan, toutes les fonctionnalités. Le tarif fondateur récompense les premiers utilisateurs.";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="landing-page">
      {/* Topbar */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-zinc-950/80 border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="topbar-logo">
            <img src="/brand/logo.png" alt="Home MDB 2A" className="h-12 w-auto object-contain" />
            <span className="font-bold text-lg tracking-tight hidden sm:inline">Home <span className="text-amber-400">MDB 2A</span></span>
          </Link>
          <nav className="flex items-center gap-3 sm:gap-5">
            <a href="#features" className="hidden md:inline text-sm text-zinc-400 hover:text-white transition-colors">Fonctionnalités</a>
            <a href="#story" className="hidden md:inline text-sm text-zinc-400 hover:text-white transition-colors">Mon histoire</a>
            <a href="#pricing" className="hidden md:inline text-sm text-zinc-400 hover:text-white transition-colors">Prix</a>
            <Link to="/login" className="text-sm text-zinc-300 hover:text-white transition-colors px-3 py-2" data-testid="topbar-login">Se connecter</Link>
            <Link
              to="/signup"
              className="text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-zinc-950 px-4 py-2 rounded-lg transition-all hover:shadow-lg hover:shadow-amber-500/20"
              data-testid="topbar-signup"
            >
              Essai gratuit 14j
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,176,22,0.12),transparent_50%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(245,176,22,0.06),transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            {founderOpen && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-semibold mb-6" data-testid="hero-founder-badge">
                <Flame className="w-3.5 h-3.5" />
                Tarif fondateur 39€/mois — <strong>{founders.remaining}/30 places restantes</strong>
              </div>
            )}
            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight leading-[1.05]">
              {heroTitle1} <span className="text-amber-400">{heroHighlight}</span>.<br />
              <span className="text-zinc-500">{heroTitle2}</span>
            </h1>
            <p className="mt-6 text-xl text-zinc-400 leading-relaxed max-w-2xl">
              {heroSubtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-all hover:shadow-xl hover:shadow-amber-500/30 hover:-translate-y-0.5"
                data-testid="hero-cta-signup"
              >
                Démarrer mon essai gratuit
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 font-medium transition-colors"
              >
                Voir les fonctionnalités
              </a>
            </div>
            <div className="mt-6 flex items-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400" /> 14 jours sans CB</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400" /> Sans engagement</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-amber-400" /> Données en France 🇫🇷</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features intro */}
      <section id="features" className="pt-24 pb-12 border-t border-zinc-900">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-amber-400 text-sm font-bold uppercase tracking-widest mb-3">16 outils intégrés · zéro logiciel externe · 100% pensé pour les MdB</p>
          <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
            Tout le métier de MDB, dans <span className="text-amber-400">un seul outil</span>.
          </h2>
          <p className="mt-5 text-zinc-400 text-lg leading-relaxed max-w-3xl mx-auto">
            Conçue à partir des vraies questions du terrain : combien je vais marger ?
            Le bien tient la route ? Qui a chiffré ce poste ? Le chantier est en dépassement ?
            Chaque fonctionnalité existe parce qu'elle a manqué à un MDB en activité.
          </p>
        </div>
      </section>

      {/* Features détaillées (chaque onglet) */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 space-y-28">
          {featuresMerged.map((feature, i) => (
            <FeatureRow key={i} feature={feature} index={i} />
          ))}
        </div>
      </section>

      {/* Story */}
      <section id="story" className="py-24 border-t border-zinc-900 bg-zinc-900/30">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start">
            <div className="md:col-span-1">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-amber-500/15 to-amber-700/10 border border-amber-500/20 flex items-center justify-center overflow-hidden relative">
                <img src="/brand/logo.png" alt="Home MDB 2A" className="w-3/4 h-3/4 object-contain opacity-90" />
              </div>
              <div className="mt-6 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-400"><Building className="w-4 h-4 text-amber-400" /> Marchand de Biens en activité</div>
                <div className="flex items-center gap-2 text-zinc-400"><Wrench className="w-4 h-4 text-amber-400" /> 480 m² rénovés en propre</div>
                <div className="flex items-center gap-2 text-zinc-400"><ShieldCheck className="w-4 h-4 text-amber-400" /> Outil utilisé sur ses propres opérations</div>
              </div>
            </div>
            <div className="md:col-span-2">
              <p className="text-amber-400 text-sm font-bold uppercase tracking-widest mb-3">Mon histoire</p>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                Pourquoi j'ai construit <span className="text-amber-400">Home MDB 2A</span>
              </h2>
              <div className="space-y-5 text-zinc-300 text-base lg:text-lg leading-relaxed">
                {storyParagraphs ? (
                  storyParagraphs.map((p, i) => (
                    <p
                      key={`story-paragraph-${i}`}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(p, {
                          ALLOWED_TAGS: ['strong', 'em', 'br', 'b', 'i', 'span'],
                          ALLOWED_ATTR: ['class'],
                        }),
                      }}
                    />
                  ))
                ) : (
                  <>
                    <p>
                      Pendant plusieurs années, j'ai été <strong className="text-white">conducteur de travaux dans les télécoms</strong>. Le quotidien : piloter des chantiers, gérer des équipes, des budgets, des dépassements. La rigueur du terrain, la lecture des chiffres, la coordination des corps de métier — tout ça m'est resté.
                    </p>
                    <p>
                      En parallèle, j'ai <strong className="text-amber-400">entièrement rénové ma résidence principale</strong> : la transformation d'un corps de ferme de 480 m² en une maison d'habitation, deux logements locatifs et un atelier de production. Pas un chantier confié à un général : ma main, mon plan, mon budget.
                    </p>
                    <p>
                      De cette opération est née une évidence : <strong className="text-white">le métier de Marchand de biens, c'est exactement ça</strong> — voir le potentiel d'un bâtiment, chiffrer juste, exécuter, revendre. J'ai fondé ma société et choisi ce métier pour de bon.
                    </p>
                    <p>
                      Mais sur le terrain, je perdais des heures. Excel sur 12 onglets pour la rentabilité, notes papier dans la voiture, devis griffonnés sur des coins de feuille, photos noyées dans la galerie, BDC en Word retravaillés un par un. <strong className="text-white">Aucun outil n'était pensé pour <em>vraiment</em> notre métier</strong> : ils étaient soit des CRM immobiliers d'agence (inadaptés), soit des feuilles Excel partagées (rigides, isolées, sans mode terrain).
                    </p>
                    <p>
                      Alors j'ai construit l'outil que je rêvais d'avoir. Celui qui parle vraiment notre métier — qui calcule l'IS au lieu de l'IRPP, qui chiffre depuis un BPU au lieu de re-saisir, qui fonctionne hors ligne dans la cave d'un bien à visiter, qui génère le BDC artisan au bon format dès qu'on passe le compromis.
                    </p>
                  </>
                )}
                <p className="text-amber-400 italic font-medium pt-2 border-t border-amber-500/20">
                  "{storyQuote}"
                </p>
                <p className="text-right text-sm text-zinc-500">— <strong className="text-zinc-300">Axel AGUANNO</strong>, fondateur</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why this app */}
      <section className="py-24 border-t border-zinc-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Zap, title: "Pensée terrain", text: "Mode hors ligne complet sur smartphone, BPU autocomplete pour visiter en 20 min, photos annotées par poste — la techno disparaît, le métier prend le dessus." },
              { icon: ShieldCheck, title: "Chiffres fiables", text: "Régime IS progressif, TVA marge ou normale, frais de structure au prorata, portage bancaire calculé sur la durée réelle. Plus jamais de surprise après l'acte." },
              { icon: Star, title: "Évolutive avec ses utilisateurs", text: "Chaque retour de MDB utilisateur fait l'objet d'une mise à jour. La roadmap est publique, le développeur est lui-même MDB en activité." },
            ].map(({ icon: Icon, title, text }, i) => (
              <div key={i} className="text-center">
                <div className="inline-flex w-14 h-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="font-semibold text-xl mb-2">{title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages — affichés seulement si au moins 1 avis public */}
      {reviews.length > 0 && (
        <section id="testimonials" className="py-24 border-t border-zinc-900 bg-zinc-900/30">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-amber-400 text-sm font-bold uppercase tracking-widest mb-3">Ils utilisent Home MDB 2A</p>
              <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
                Ce que disent <span className="text-amber-400">les MDB</span>
              </h2>
              {reviews.length >= 3 && (
                <div className="mt-5 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30">
                  <div className="flex items-center gap-1">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-sm text-zinc-300">
                    <strong className="text-amber-400">
                      {(reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)}/5
                    </strong>
                    {' '}de moyenne sur {reviews.length} avis
                  </span>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.slice(0, 6).map((r, i) => (
                <div
                  key={i}
                  className={`relative rounded-2xl p-6 border transition-all hover:-translate-y-1 ${
                    r.featured
                      ? 'bg-gradient-to-br from-amber-500/10 to-zinc-900 border-amber-500/30 shadow-xl shadow-amber-500/5'
                      : 'bg-zinc-900/50 border-zinc-800'
                  }`}
                  data-testid={`landing-review-${i}`}
                >
                  {r.featured && (
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-amber-500 text-zinc-950 flex items-center justify-center shadow-lg">
                      <Sparkles className="w-4 h-4" />
                    </div>
                  )}
                  <Quote className="w-7 h-7 text-amber-400/50 mb-3" />
                  <div className="flex mb-3">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? 'fill-amber-400 text-amber-400' : 'text-zinc-700'}`} />
                    ))}
                  </div>
                  {r.comment ? (
                    <p className="text-zinc-300 text-sm leading-relaxed mb-4 whitespace-pre-wrap">"{r.comment}"</p>
                  ) : (
                    <p className="text-zinc-600 text-sm italic mb-4">Pas de commentaire</p>
                  )}
                  <p className="text-xs text-zinc-500 font-mono">— {r.display_name || 'Utilisateur'}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing — 2 cartes : Fondateur + Standard */}
      <section id="pricing" className="py-24 border-t border-zinc-900 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-amber-400 text-sm font-bold uppercase tracking-widest mb-3">Tarification</p>
            <h2 className="text-4xl lg:text-5xl font-bold tracking-tight">
              Simple. <span className="text-amber-400">Tout inclus</span>.
            </h2>
            <p className="mt-4 text-zinc-400 text-lg">{pricingIntro}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {/* CARTE FONDATEUR */}
            <div
              className={`relative rounded-3xl p-8 lg:p-10 transition-all ${
                founderOpen
                  ? 'bg-gradient-to-br from-amber-500/10 to-zinc-900 border-2 border-amber-500/40 shadow-2xl shadow-amber-500/10 scale-100 lg:scale-[1.02]'
                  : 'bg-zinc-900/60 border-2 border-zinc-800 opacity-60'
              }`}
              data-testid="pricing-card-founder"
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-zinc-950 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5">
                <Flame className="w-3 h-3" />
                {founderOpen ? `${founders.remaining}/30 places restantes` : 'Complet'}
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-1">Tarif Fondateur</h3>
                <p className="text-zinc-400 text-sm">Pour les 30 premiers MDB qui rejoignent l'aventure</p>
                <div className="mt-6 flex items-baseline justify-center gap-2">
                  <span className="text-6xl font-bold text-amber-400">{founders.founder_price_eur}€</span>
                  <span className="text-zinc-500 text-lg">/mois</span>
                </div>
                <p className="text-sm text-amber-400/80 mt-2 font-semibold">Tarif verrouillé à vie 🔒</p>
              </div>

              <div className="rounded-xl bg-zinc-950/60 border border-amber-500/20 p-3 mb-6 text-center">
                <p className="text-xs text-zinc-400">Places restantes en temps réel</p>
                <p className="text-3xl font-bold text-amber-400 mt-1" data-testid="pricing-remaining">{founderOpen ? `${founders.remaining} / 30` : '0 / 30'}</p>
                <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
                    style={{ width: `${Math.min(100, ((30 - founders.remaining) / 30) * 100)}%` }}
                  />
                </div>
              </div>

              <ul className="space-y-2.5 mb-8">
                {PRICING_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-amber-400" />
                    </div>
                    <span className="text-zinc-200 text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className={`block w-full text-center py-4 rounded-xl font-bold text-lg transition-all ${
                  founderOpen
                    ? 'bg-amber-500 hover:bg-amber-400 text-zinc-950 hover:shadow-xl hover:shadow-amber-500/30'
                    : 'bg-zinc-800 text-zinc-500 cursor-not-allowed'
                }`}
                data-testid="pricing-cta-founder"
                onClick={(e) => { if (!founderOpen) e.preventDefault(); }}
              >
                {founderOpen ? 'Réserver ma place fondateur' : 'Places fondateurs épuisées'}
              </Link>
              <p className="text-center text-xs text-zinc-500 mt-3">14 jours d'essai gratuit · Sans CB · Annulable en 1 clic</p>
            </div>

            {/* CARTE STANDARD */}
            <div className="relative rounded-3xl p-8 lg:p-10 bg-zinc-900/40 border border-zinc-800" data-testid="pricing-card-standard">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-zinc-700 text-zinc-300 text-xs font-bold uppercase tracking-wider">
                Tarif standard
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-1">Plan Standard</h3>
                <p className="text-zinc-400 text-sm">Une fois les 30 places fondateurs prises</p>
                <div className="mt-6 flex items-baseline justify-center gap-2">
                  <span className="text-6xl font-bold text-zinc-300">{founders.standard_price_eur}€</span>
                  <span className="text-zinc-500 text-lg">/mois</span>
                </div>
                <p className="text-sm text-zinc-500 mt-2">Strictement les mêmes fonctionnalités</p>
              </div>

              <div className="rounded-xl bg-zinc-950/40 border border-zinc-800 p-3 mb-6 text-center">
                <p className="text-xs text-zinc-500">Disponible</p>
                <p className="text-base font-semibold text-zinc-300 mt-1">
                  {founderOpen ? 'Après les 30 premières places' : 'Maintenant 🚀'}
                </p>
              </div>

              <ul className="space-y-2.5 mb-8">
                {PRICING_FEATURES.map((f, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-zinc-400" />
                    </div>
                    <span className="text-zinc-300 text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/signup"
                className="block w-full text-center py-4 rounded-xl font-semibold text-base bg-zinc-800 hover:bg-zinc-700 text-zinc-200 transition-colors"
                data-testid="pricing-cta-standard"
              >
                {founderOpen ? 'Préfère le tarif standard' : 'Démarrer l\'essai gratuit'}
              </Link>
              <p className="text-center text-xs text-zinc-500 mt-3">14 jours d'essai gratuit · Sans CB · Annulable en 1 clic</p>
            </div>
          </div>

          {founderOpen && (
            <p className="mt-10 text-center text-sm text-zinc-500 max-w-2xl mx-auto">
              <strong className="text-amber-400">⚡ Le tarif fondateur 39€/mois est verrouillé à vie pour les 30 premiers MDB.</strong>
              {' '}Quand les 30 places seront prises, le tarif standard sera de {founders.standard_price_eur}€/mois.
              Les fondateurs gardent leur tarif initial, sans augmentation, tant qu'ils restent abonnés.
            </p>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 border-t border-zinc-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Tu veux savoir si <span className="text-amber-400">ton prochain projet est rentable</span> ?
          </h2>
          <p className="text-xl text-zinc-400 mb-10">
            Lance ton premier projet dans l'app en 5 minutes. Sans CB. Sans engagement.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold text-lg transition-all hover:shadow-xl hover:shadow-amber-500/30 hover:-translate-y-0.5"
            data-testid="final-cta"
          >
            Démarrer mon essai gratuit
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-900 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/brand/logo.png" alt="Home MDB 2A" className="h-10 w-auto object-contain" />
              <span className="font-bold tracking-tight">Home <span className="text-amber-400">MDB 2A</span></span>
            </div>
            <div className="flex flex-wrap gap-6 text-sm text-zinc-500">
              <Link to="/cgu" className="hover:text-amber-400 transition-colors">CGU</Link>
              <Link to="/cgv" className="hover:text-amber-400 transition-colors">CGV</Link>
              <Link to="/confidentialite" className="hover:text-amber-400 transition-colors">Confidentialité (RGPD)</Link>
              <Link to="/mentions-legales" className="hover:text-amber-400 transition-colors">Mentions légales</Link>
            </div>
          </div>
          <p className="mt-6 text-xs text-zinc-600">
            © {new Date().getFullYear()} Home MDB 2A — Faite par Axel AGUANNO. Données hébergées en France 🇫🇷
          </p>
        </div>
      </footer>
    </div>
  );
}
